import BreadCrumb from '@medical/components/Breadcrumb'
import isValidCsvDate from '@medical/libs/isValidCsvDate'
import isValidCsvTime from '@medical/libs/isValidCsvTime'
import { FieldArray, Formik } from 'formik'
import Papa from 'papaparse'
import { Button } from 'primereact/button'
import React from 'react'
import Dropzone from 'react-dropzone'
import * as Yup from 'yup'

import UnpublishedShiftConfirmDialog from './UnpublishedShiftConfirmDialog'

export const createCsvDateSchema = requiredMessage =>
  Yup.string()
    .required(requiredMessage)
    .test(
      'is-valid-date',
      '正しい日付を入力してください',
      value => !value || isValidCsvDate(value)
    )

export const createCsvTimeSchema = (label, requiredMessage) => {
  const schema = Yup.string().test(
    'is-valid-time',
    `${label}に正しい時間を入力してください`,
    value => !value || isValidCsvTime(value)
  )
  return requiredMessage ? schema.required(requiredMessage) : schema
}

export const createCsvIntegerSchema = ({
  label,
  requiredMessage,
  min,
  max,
  rangeMessage,
}) => {
  const schema = Yup.string()
    .test(
      'is-integer',
      `${label}は整数で入力してください`,
      value => !value || /^-?\d+$/.test(String(value).trim())
    )
    .test(
      'is-in-range',
      rangeMessage,
      value =>
        !value ||
        ((!Number.isFinite(min) || Number(value) >= min) &&
          (!Number.isFinite(max) || Number(value) <= max))
    )
  return requiredMessage ? schema.required(requiredMessage) : schema
}

export const createCsvBooleanSchema = (label, requiredMessage) => {
  const schema = Yup.mixed().oneOf(
    ['TRUE', 'true', '1', 'FALSE', 'false', '0', true, false, 1, 0, ''],
    `${label}はTRUE、FALSE、1、0のいずれかで入力してください`
  )
  return requiredMessage ? schema.required(requiredMessage) : schema
}

export const isBlankCsvRow = row =>
  Object.keys(row).every(
    key => row[key] == null || String(row[key]).trim() === ''
  )

export const getCsvUploadBlockReason = ({ errors, data }) => {
  if (
    (errors.data && errors.data.length > 0) ||
    data.some(row => row.errorMessage)
  ) {
    return 'validation-error'
  }
  if (data.some(row => !row.checked)) return 'not-checked'
  return null
}

const WorkScheduleCsvScene = ({
  popup,
  i18n,
  downloadClinicalDepartments,
  onSubmit,
  downloadSample,
  isAvailableShift,
  isUploaded,
  checkCsvData,
  isUploadPermitted,
  isUploadCheckPermitted,
  unpublishedSummary,
  onCancelUnpublished,
  onConfirmUnpublished,
}) => {
  // Papa.parse reads the file asynchronously, and until it finishes the row
  // data is empty, which keeps the check button disabled. The button stays on
  // screen looking the same, so a click in that window is dropped without any
  // feedback: measured at 516ms for a one row file and 745ms for 600 rows on
  // staging. Saying the file is being read is what closes that gap.
  const [isParsing, setIsParsing] = React.useState(false)
  const translateKeys = {
    numberOfRooms: i18n.t('staff.createShift.numberOfRooms'),
    startTime: i18n.t('staff.createShift.startTime'),
    endTime: i18n.t('staff.createShift.endTime'),
    hourlyWage: i18n.t('main.downloadWorkSchedule.hourlyWage'),
    splitDateTime1: i18n.t('main.downloadWorkSchedule.splitDateTime1'),
    splitDateTime2: i18n.t('main.downloadWorkSchedule.splitDateTime2'),
    splitDateTime3: i18n.t('main.downloadWorkSchedule.splitDateTime3'),
    splitHourlyWage1: i18n.t('main.downloadWorkSchedule.splitHourlyWage1'),
    splitHourlyWage2: i18n.t('main.downloadWorkSchedule.splitHourlyWage2'),
    splitHourlyWage3: i18n.t('main.downloadWorkSchedule.splitHourlyWage3'),
    delete: i18n.t('doctor.availableShifts.removeItem'),
    deleteCount: '削除予定件数',
    departmentCode: i18n.t('main.departmentCode'),
    email: i18n.t('updateProfile.email'),
    date: i18n.t('staff.createShift.date'),
    comment: i18n.t('main.workDetail'),
    isStartTimeBreakTime: i18n.t(
      'main.downloadWorkSchedule.isStartTimeBreakTime'
    ),
    isSplitDateTime1BreakTime: i18n.t(
      'main.downloadWorkSchedule.isSplitDateTime1BreakTime'
    ),
    isSplitDateTime2BreakTime: i18n.t(
      'main.downloadWorkSchedule.isSplitDateTime2BreakTime'
    ),
    isSplitDateTime3BreakTime: i18n.t(
      'main.downloadWorkSchedule.isSplitDateTime3BreakTime'
    ),
    isPublished: '掲載/未掲載',
    isDoubleRecruitment: '二診目登録',
    doctorSubsidy1Title: '追加支給1（項目名）',
    doctorSubsidy1Money: '追加支給1（金額）',
    doctorSubsidy2Title: '追加支給2（項目名）',
    doctorSubsidy2Money: '追加支給2（金額）',
    doctorSubsidy3Title: '追加支給3（項目名）',
    doctorSubsidy3Money: '追加支給3（金額）',
    doctorSubsidy4Title: '追加支給4（項目名）',
    doctorSubsidy4Money: '追加支給4（金額）',
    doctorSubsidy5Title: '追加支給5（項目名）',
    doctorSubsidy5Money: '追加支給5（金額）',
  }
  const validationSchema = Yup.object().shape({
    data: Yup.array()
      .min(1, i18n.t('validation.atLeastOneItem'))
      .of(
        Yup.object().shape({
          email: isAvailableShift
            ? undefined
            : Yup.string()
                .email(i18n.t('validation.email.email'))
                .required(i18n.t('validation.email.required')),
          date: createCsvDateSchema(
            `${i18n.t('main.date')} ${i18n.t('validation.required')}`
          ),
          startTime: createCsvTimeSchema(
            translateKeys.startTime,
            `${i18n.t('main.startTime')} ${i18n.t('validation.required')}`
          ),
          endTime: createCsvTimeSchema(
            translateKeys.endTime,
            `${i18n.t('main.endTime')} ${i18n.t('validation.required')}`
          ),
          splitDateTime1: createCsvTimeSchema(translateKeys.splitDateTime1),
          splitDateTime2: createCsvTimeSchema(translateKeys.splitDateTime2),
          splitDateTime3: createCsvTimeSchema(translateKeys.splitDateTime3),
          hourlyWage: createCsvIntegerSchema({
            label: translateKeys.hourlyWage,
            requiredMessage: i18n.t('validation.hourlyWage.required'),
            min: 0,
            max: 99999,
            rangeMessage: `${translateKeys.hourlyWage}は0円以上10万円未満で入力してください`,
          }),
          splitHourlyWage1: createCsvIntegerSchema({
            label: translateKeys.splitHourlyWage1,
            min: 0,
            max: 99999,
            rangeMessage: `${translateKeys.splitHourlyWage1}は0円以上10万円未満で入力してください`,
          }),
          splitHourlyWage2: createCsvIntegerSchema({
            label: translateKeys.splitHourlyWage2,
            min: 0,
            max: 99999,
            rangeMessage: `${translateKeys.splitHourlyWage2}は0円以上10万円未満で入力してください`,
          }),
          splitHourlyWage3: createCsvIntegerSchema({
            label: translateKeys.splitHourlyWage3,
            min: 0,
            max: 99999,
            rangeMessage: `${translateKeys.splitHourlyWage3}は0円以上10万円未満で入力してください`,
          }),
          numberOfRooms: isAvailableShift
            ? createCsvIntegerSchema({
                label: translateKeys.numberOfRooms,
                requiredMessage: i18n.t('validation.numberOfRooms.required'),
                min: 1,
                max: 10,
                rangeMessage: `${translateKeys.numberOfRooms}は1以上10以下で入力してください`,
              })
            : undefined,
          // Recruitment shifts accept the delete column too, so the rule is no
          // longer restricted to confirmed shifts.
          delete: Yup.mixed()
            .nullable()
            .test(
              'isDelete',
              `${i18n.t('validation.isDelete')}`,
              value => value === '1' || !value
            ),
          comment: Yup.string(),
          isStartTimeBreakTime: createCsvBooleanSchema(
            translateKeys.isStartTimeBreakTime,
            i18n.t('validation.breakTime.required')
          ),
          isSplitDateTime1BreakTime: createCsvBooleanSchema(
            translateKeys.isSplitDateTime1BreakTime
          ),
          isSplitDateTime2BreakTime: createCsvBooleanSchema(
            translateKeys.isSplitDateTime2BreakTime
          ),
          isSplitDateTime3BreakTime: createCsvBooleanSchema(
            translateKeys.isSplitDateTime3BreakTime
          ),
          isPublished: Yup.string()
            .nullable()
            .oneOf(
              ['TRUE', 'true', '1', '掲載', 'FALSE', 'false', '0', '未掲載', ''],
              '掲載/未掲載の値が正しくありません'
            ),
          isDoubleRecruitment: Yup.string()
            .nullable()
            .oneOf(
              ['TRUE', 'true', '1', '二診目', 'FALSE', 'false', '0', ''],
              '二診目登録の値が正しくありません'
            ),
          doctorSubsidy1Title: Yup.string(),
          doctorSubsidy1Money: createCsvIntegerSchema({
            label: translateKeys.doctorSubsidy1Money,
            min: 0,
            rangeMessage: '0以上の整数で指定してください',
          }),
          doctorSubsidy2Title: Yup.string(),
          doctorSubsidy2Money: createCsvIntegerSchema({
            label: translateKeys.doctorSubsidy2Money,
            min: 0,
            rangeMessage: '0以上の整数で指定してください',
          }),
          doctorSubsidy3Title: Yup.string(),
          doctorSubsidy3Money: createCsvIntegerSchema({
            label: translateKeys.doctorSubsidy3Money,
            min: 0,
            rangeMessage: '0以上の整数で指定してください',
          }),
          doctorSubsidy4Title: Yup.string(),
          doctorSubsidy4Money: createCsvIntegerSchema({
            label: translateKeys.doctorSubsidy4Money,
            min: 0,
            rangeMessage: '0以上の整数で指定してください',
          }),
          doctorSubsidy5Title: Yup.string(),
          doctorSubsidy5Money: createCsvIntegerSchema({
            label: translateKeys.doctorSubsidy5Money,
            min: 0,
            rangeMessage: '0以上の整数で指定してください',
          }),
        })
      ),
  })

  const exceptKeys = ['checked', 'errorMessage']
  return (
    <Formik
      initialValues={{
        data: [],
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      render={formikProps => {
        const {
          values,
          errors,
          setFieldValue,
          handleSubmit,
          touched,
        } = formikProps
        const { data } = values
        let keys = []
        if (data.length > 0) {
          
          keys = Object.keys(data[0]).filter(
            key => key !== 'errorMessage' || key !== 'checked'
          )
        }
        const titles = {
          true: i18n.t('staff.menuBar.titleAvailableShiftCSV'),
          false: i18n.t('staff.menuBar.titleWorkSchedule'),
        }
        
        return (
          <React.Fragment>
            <div className='staff-header'>
              <div className='staff-title'>
                {titles[isAvailableShift]}
                <BreadCrumb
                  items={[
                    {
                      title: titles[isAvailableShift],
                    },
                  ]}
                />
              </div>
              <div className='staff-buttons'>
                <Button
                  icon='pi pi-download'
                  className='p-button-normal'
                  onClick={downloadClinicalDepartments}
                  label={i18n.t('main.clinicalDepartment')}
                />
                <Button
                  icon='pi pi-download'
                  className='p-button-normal'
                  onClick={downloadSample}
                  label={i18n.t('main.sampleCsv')}
                />
              </div>
            </div>
            {isUploadPermitted || isUploadCheckPermitted ? (
              <div className='container'>
                <div className='box'>
                  <div className='box-top'>
                    <div
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flex: 1,
                        alignItems: 'center',
                      }}
                    >
                      <div className='alert is-left'>
                        {typeof errors.data === 'string' &&
                          touched.data &&
                          errors.data}
                      </div>
                    </div>
                    {values.data.length > 0 && (
                      <Button
                        onClick={() => setFieldValue('data', [])}
                        label={i18n.t('main.cancel')}
                        className='p-button-secondary'
                        icon='pi pi-times'
                        style={{ marginRight: '20px' }}
                      />
                    )}
                    {isUploadCheckPermitted ? (
                      <Button
                        onClick={() => {
                          if (
                            (errors.data && errors.data.length > 0) ||
                            values.data.find(d => d.errorMessage)
                          ) {
                            popup.error(i18n.t('main.hasCsvFileError'))
                          } else {
                            checkCsvData(values, { setFieldValue })
                          }
                        }}
                        label={i18n.t('staff.csvUpload.check')}
                        type='submit'
                        disabled={data.length === 0 || isUploaded}
                        style={{ marginRight: '20px' }}
                      />
                    ) : null}
                    {isUploadPermitted ? (
                      <Button
                        onClick={() => {
                          const blockReason = getCsvUploadBlockReason({
                            errors,
                            data: values.data,
                          })
                          if (blockReason === 'validation-error') {
                            popup.error(i18n.t('main.hasCsvFileError'))
                          } else if (blockReason === 'not-checked') {
                            popup.error(i18n.t('staff.csvUpload.notChecked'))
                          } else {
                            handleSubmit()
                          }
                        }}
                        label={i18n.t('staff.csvUpload.submit')}
                        type='submit'
                        disabled={data.length === 0 || isUploaded}
                      />
                    ) : null}
                  </div>
                  {values.data.length < 1 && (
                    <Dropzone
                      onDrop={files => {
                        if (files.length > 0) {
                          setIsParsing(true)
                          Papa.parse(files[0], {
                            header: true,
                            skipEmptyLines: true,
                            complete: async results => {
                              setFieldValue(
                                'data',
                                results.data.filter(row => !isBlankCsvRow(row))
                              )
                              setIsParsing(false)
                            },
                            error: () => setIsParsing(false),
                          })
                        }
                      }}
                      disabled={isParsing}
                      accept='.csv'
                    >
                      {({ getRootProps, getInputProps }) => (
                        <div {...getRootProps({ className: 'dropzone' })}>
                          <input {...getInputProps()} />
                          <p>
                            {isParsing
                              ? i18n.t('staff.csvUpload.parsing')
                              : i18n.t('doctor.uploadFile.dropFileHere')}
                          </p>
                        </div>
                      )}
                    </Dropzone>
                  )}
                  {data.length > 0 && (
                    <div className='overflow-container'>
                      <table className='table'>
                        <thead>
                          <tr>
                            <th>{i18n.t('main.action')}</th>
                            {keys.map(
                              (key, index) =>
                                !exceptKeys.includes(key) && (
                                  <th key={`${key}_${index}`}>
                                    {translateKeys[key]}
                                  </th>
                                )
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          <FieldArray
                            name='data'
                            render={({ remove }) => (
                              <React.Fragment>
                                
                                {data.map((item, i) => {
                                  
                                  const error =
                                    errors.data && errors.data.length > 0
                                      ? errors.data[i]
                                      : false
                                    
                                  const isChecked = data[i].checked
                                  const errorMessage =
                                    item.errorMessage ||
                                    Object.keys(error || {}).reduce(
                                      (acc, curr) => {
                                        
                                        
                                        return `${acc}\n${error[curr]}`
                                      } ,
                                      ''
                                    )
                                    
                        
                                  return (
                                    <React.Fragment>
                                      <tr key={`${i}`}>
                                        {!errorMessage ? (
                                          <td>
                                            {isChecked ? (
                                              <Button
                                                icon='pi pi-check'
                                                className='p-button-success'
                                              />
                                            ) : (
                                              <Button
                                                icon='pi pi-info'
                                                tooltip={i18n.t(
                                                  'staff.csvUpload.notChecked'
                                                )}
                                              />
                                            )}
                                          </td>
                                        ) : (
                                          <td>
                                            <div>
                                              <Button
                                                onClick={() => remove(i)}
                                                icon='pi pi-times'
                                                className='p-button-danger'
                                              />
                                            </div>
                                          </td>
                                        )}
                                        {keys.map(
                                          key =>
                                            !exceptKeys.includes(key) && (
                                              <td>{item[key]}</td>
                                            )
                                        )}
                                      </tr>
                                      {errorMessage && (
                                        <tr key={`errorMessage_${i}`}>
                                          <td colSpan={keys.length + 1}>
                                            <div className='alert is-left'>
                                              {errorMessage}
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  )
                                })}
                              </React.Fragment>
                            )}
                          />
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : null}
            <UnpublishedShiftConfirmDialog
              summary={unpublishedSummary}
              isSubmitting={isUploaded}
              onCancel={onCancelUnpublished}
              onConfirm={onConfirmUnpublished}
            />
          </React.Fragment>
        )
      }}
    />
  )
}

export default WorkScheduleCsvScene
